import { UserTypes } from '../../../types';

export type LastPingSource = 'device' | 'app';

export const LAST_PING_SOURCE_LABEL: Record<LastPingSource, string> = {
    device: 'POM Device',
    app: 'Mobile App',
};

// Millisecond timestamp, or null when missing/invalid. Mirrors the
// year-2000 sanity floor used by formatRelativePing — legacy records
// can carry epoch-zero placeholder dates.
const toValidTime = (raw: Date | string | null | undefined): number | null => {
    if (!raw) return null;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t) || new Date(t).getFullYear() < 2000) return null;
    return t;
};

const maxTime = (times: (number | null)[]): number | null => {
    const valid = times.filter((t): t is number => t !== null);
    return valid.length ? Math.max(...valid) : null;
};

// node-api's heartbeat handler writes lastHeartbeatTimestamp for BOTH app
// heartbeats and device-relayed (IoT) heartbeats, so it is a trustworthy
// TIME signal but ambiguous as a SOURCE signal — deriveLastPingSource must
// not treat it as app evidence.
const locationPingTime = (user: UserTypes.Model | null | undefined): number | null =>
    toValidTime(user?.mobileLocation?.updatedAt);

const heartbeatPingTime = (user: UserTypes.Model | null | undefined): number | null =>
    toValidTime(user?.lastHeartbeatTimestamp);

// The paired POM device's last update, across all of the user's devices.
const devicePingTime = (user: UserTypes.Model | null | undefined): number | null =>
    maxTime((user?.devices ?? []).map((device) => toValidTime(device?.updatedAt)));

// Most recent REAL heartbeat for a user, across every signal we have:
// the app's location ping, the location-less heartbeat, each paired
// device's update, and any caller-supplied extras (e.g. the appointments
// payload's server-computed lastDevicePing — appointment endpoints strip
// user.devices, so the device leg must be passed in). Returns null when
// no heartbeat was ever recorded. Never falls back to user.updatedAt:
// that is bumped by any admin/bulk write (tags, team assignment,
// imports), which made "Last Ping" over-report activity during bulk
// operations.
export const deriveLastPingTime = (
    user: UserTypes.Model | null | undefined,
    extraPings: Array<Date | string | null | undefined> = [],
): Date | null => {
    const latest = maxTime([
        locationPingTime(user),
        heartbeatPingTime(user),
        devicePingTime(user),
        ...extraPings.map(toValidTime),
    ]);
    return latest === null ? null : new Date(latest);
};

// The user payload has no explicit ping-source field, so the source is
// inferred from whichever signal is most recent. Only mobileLocation is
// app evidence and only devices[] is device evidence; when the winner is
// lastHeartbeatTimestamp (set by both app and device-relayed heartbeats)
// the source is unknowable and we return null so callers don't claim a
// source we can't back up. Ties keep the legacy device-wins behavior.
export const deriveLastPingSource = (
    user: UserTypes.Model,
): LastPingSource | null => {
    const appPing = locationPingTime(user);
    const devicePing = devicePingTime(user);
    const ambiguous = heartbeatPingTime(user);

    const winner = maxTime([appPing, devicePing, ambiguous]);
    if (winner === null) return null;
    if (devicePing === winner) return 'device';
    if (appPing === winner) return 'app';
    return null;
};
